'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { LOGO_DARK_SRC, LOGO_LIGHT_SRC, SITE_NAME } from '@/lib/site';

/** Display sizes — logos are square; use width in narrow sidebars for readability */
export const BRAND_LOGO_SIZES = {
  sidebar: { width: 150 },
  nav: { height: 110 },
  footer: { height: 185 },
  auth: { height: 160 },
  loader: { height:80 },
  md: { height: 56 },
};

function resolveTheme(variant) {
  if (variant === 'dark' || variant === 'light') return variant;
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.classList.contains('light-theme') ? 'light' : 'dark';
}

/**
 * Approved MineMe logo — logo-dark on dark UI, logo-light on light UI.
 * @param {{ href?: string, height?: number, width?: number, size?: keyof typeof BRAND_LOGO_SIZES, variant?: 'auto'|'dark'|'light', className?: string, priority?: boolean }} props
 */
export default function BrandLogo({
  href,
  height: heightProp,
  width: widthProp,
  size,
  variant = 'auto',
  className = '',
  priority = false,
}) {
  const preset = size ? BRAND_LOGO_SIZES[size] : null;
  const width = widthProp ?? preset?.width;
  const height = heightProp ?? preset?.height ?? BRAND_LOGO_SIZES.md.height;

  const [theme, setTheme] = useState(() => (variant === 'auto' ? 'dark' : variant));

  useEffect(() => {
    if (variant !== 'auto') {
      setTheme(variant);
      return undefined;
    }
    const read = () => setTheme(resolveTheme('auto'));
    read();
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, [variant]);

  const src = theme === 'light' ? LOGO_LIGHT_SRC : LOGO_DARK_SRC;
  const centered = size === 'sidebar';

  const style = width
    ? { width: `${width}px`, height: 'auto', maxWidth: '100%' }
    : { height: `${height}px`, width: 'auto', maxWidth: 'min(100%, 320px)' };

  const imgClass = [
    'block object-contain',
    centered ? 'mx-auto object-center' : 'object-left',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const img = (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={`${SITE_NAME} logo`}
      decoding="async"
      fetchPriority={priority ? 'high' : 'auto'}
      className={imgClass}
      style={style}
    />
  );

  if (!href) return img;

  return (
    <Link
      href={href}
      className={
        centered
          ? 'flex w-full justify-center no-underline'
          : 'inline-flex shrink-0 items-center no-underline'
      }
    >
      {img}
    </Link>
  );
}
